import { inject } from '@angular/core';
import { VehiclesApi } from '@app/api/vehicle.api';
import { CreateVehicleRequest, VehicleDto, VehicleType } from '@app/models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, map, pipe, switchMap, tap } from 'rxjs';
import { ShareVehicleRequest } from './../models/vehicle.model';

interface VehiclesState {
  trucks: VehicleDto[];
  trailers: VehicleDto[];
  trucksIsLoading: boolean;
  trailersIsLoading: boolean;
  trailersLoaded: boolean;
  isSubmitting: boolean;
}

const initialState: VehiclesState = {
  trucks: [],
  trailers: [],
  trucksIsLoading: false,
  trailersIsLoading: false,
  trailersLoaded: false,
  isSubmitting: false,
};

export const VehiclesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const vehiclesApi = inject(VehiclesApi);

    const loadTrucks = rxMethod<void | { onSuccess: () => void }>(
      pipe(
        tap(() => patchState(store, { trucks: [], trucksIsLoading: true })),
        switchMap((options) =>
          vehiclesApi.getVehicles$(VehicleType.Truck).pipe(
            map((trucks) => ({
              trucks,
              onSuccess: options?.onSuccess,
            })),
          ),
        ),
        tap(({ trucks, onSuccess }) => {
          patchState(store, {
            trucks,
            trucksIsLoading: false,
          }),
            onSuccess?.();
        }),
      ),
    );

    const loadTrailers = rxMethod<void | { onSuccess: () => void }>(
      pipe(
        tap(() => patchState(store, { trailers: [], trailersIsLoading: true })),
        switchMap((options) =>
          vehiclesApi.getVehicles$(VehicleType.Trailer).pipe(
            map((trailers) => ({
              trailers,
              onSuccess: options?.onSuccess,
            })),
          ),
        ),
        tap(({ trailers, onSuccess }) => {
          patchState(store, {
            trailers,
            trailersIsLoading: false,
            trailersLoaded: true,
          });

          onSuccess?.();
        }),
      ),
    );

    const addVehicle = rxMethod<{
      request: CreateVehicleRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          vehiclesApi.createVehicle$(request).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap((createdVehicle) => {
              patchState(store, (state) => ({
                trucks:
                  createdVehicle.type === VehicleType.Truck
                    ? [createdVehicle, ...state.trucks]
                    : state.trucks,

                trailers:
                  createdVehicle.type === VehicleType.Trailer
                    ? [createdVehicle, ...state.trailers]
                    : state.trailers,
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const updateVehicle = rxMethod<{
      id: number;
      request: CreateVehicleRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, request, onSubmit, onSuccess }) =>
          vehiclesApi.updateVehicle$(id, request).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap((updatedVehicle) => {
              patchState(store, (state) => ({
                trucks: state.trucks.map((truck) =>
                  updatedVehicle.id === truck.id ? updatedVehicle : truck,
                ),
                trailers: state.trailers.map((trailer) =>
                  updatedVehicle.id === trailer.id ? updatedVehicle : trailer,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const shareVehicle = rxMethod<{
      request: ShareVehicleRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          vehiclesApi.shareVehicle$(request).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap((sharedVehicle) => {
              patchState(store, (state) => ({
                trucks: state.trucks.map((truck) =>
                  sharedVehicle.id === truck.id ? sharedVehicle : truck,
                ),
                trailers: state.trailers.map((trailer) =>
                  sharedVehicle.id === trailer.id ? sharedVehicle : trailer,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const unShareVehicle = rxMethod<{
      id: number;
      vehicleType: VehicleType;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, vehicleType, onSubmit, onSuccess }) =>
          vehiclesApi
            .unshareVehicle$(id)
            .pipe(map(() => ({ vehicleType, onSubmit, onSuccess }))),
        ),
        tap(({ vehicleType, onSubmit, onSuccess }) => {
          patchState(store, { isSubmitting: false });
          onSubmit();

          vehicleType === VehicleType.Trailer
            ? loadTrailers({ onSuccess })
            : loadTrucks({ onSuccess });
        }),
      ),
    );

    const deleteVehicle = rxMethod<{
      id: number;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, onSubmit, onSuccess }) =>
          vehiclesApi.removeVehicle$(id).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap(() => {
              patchState(store, (state) => ({
                trucks: state.trucks.filter((truck) => truck.id !== id),
                trailers: state.trailers.filter((truck) => truck.id !== id),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const clearVehicles = () => patchState(store, initialState);

    return {
      loadTrucks,
      loadTrailers,
      addVehicle,
      updateVehicle,
      deleteVehicle,
      shareVehicle,
      unShareVehicle,
      clearVehicles,
    };
  }),
);
